'use client';

import { useState, useRef, useCallback, useEffect } from 'react';
import { Camera, RotateCcw } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { hubButtonStyle } from '@/components/hub/hub-ui';
import type { PhotoVariant } from './photo-flow-ui';

interface SelfieCaptureProps {
  onCapture: (dataUrl: string) => void;
  /** confirm-button label after capture (defaults to the finder's "Find Photos") */
  confirmLabel?: string;
  /** Kept for callers: both variants now draw the same v8 look (photo-flow-ui.tsx). */
  variant?: PhotoVariant;
}

export function SelfieCapture({ onCapture, confirmLabel = 'Find my photos' }: SelfieCaptureProps) {
  const isMobile = useIsMobile();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [captured, setCaptured] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);

  const startCamera = useCallback(async () => {
    try {
      setCameraError(null);
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.onloadedmetadata = () => setCameraReady(true);
      }
    } catch (err: any) {
      if (err.name === 'NotAllowedError') {
        setCameraError('Camera access denied. Please allow camera access to find your photos.');
      } else if (err.name === 'NotFoundError') {
        setCameraError('No camera found on this device.');
      } else {
        setCameraError('Unable to access camera. Please try again.');
      }
    }
  }, []);

  useEffect(() => {
    startCamera();
    return () => {
      stream?.getTracks().forEach(track => track.stop());
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startCountdown = () => {
    setCountdown(3);
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev === null || prev <= 1) {
          clearInterval(timer);
          capturePhoto();
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Mirror the image (selfie mode)
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0);

    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    setCaptured(dataUrl);

    // Stop camera
    stream?.getTracks().forEach(track => track.stop());
  };

  const retake = () => {
    setCaptured(null);
    setCountdown(null);
    startCamera();
  };

  const confirm = () => {
    if (captured) onCapture(captured);
  };

  if (cameraError) {
    return (
      <div role="alert" style={{ padding: '12px 14px', borderRadius: 'var(--fv-radius-tile, 10px)', background: 'var(--fvd-danger-tint)', textAlign: 'left' }}>
        <p style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--fvd-ink)', margin: '0 0 10px' }}>{cameraError}</p>
        <button type="button" onClick={startCamera} className="fvd-hoverable" style={hubButtonStyle('small', isMobile, { borderRadius: 'var(--fv-radius-button, 8px)' })}>
          Try again
        </button>
      </div>
    );
  }

  const waiting = !cameraReady || countdown !== null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 12 }}>
      <div style={{ position: 'relative', width: '100%', maxWidth: 400, borderRadius: 'var(--fv-radius-card, 10px)', overflow: 'hidden', background: '#000' }}>
        {!captured ? (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{ width: '100%', display: 'block', transform: 'scaleX(-1)' }}
            />
            {countdown !== null && (
              <div style={{
                position: 'absolute', inset: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(0,0,0,0.3)',
              }}>
                <span style={{ fontSize: 64, fontWeight: 600, color: '#fff', fontFamily: 'var(--fvd-font-mono)', textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
                  {countdown}
                </span>
              </div>
            )}
          </>
        ) : (
          <img src={captured} alt="Selfie preview" style={{ width: '100%', display: 'block' }} />
        )}
      </div>

      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {!captured ? (
        <button
          type="button"
          onClick={startCountdown}
          disabled={waiting}
          className={waiting ? undefined : 'fvd-hoverable-solid'}
          style={hubButtonStyle('primary', isMobile, {
            width: '100%', maxWidth: 400,
            borderRadius: 'var(--fv-radius-button, 10px)',
            opacity: waiting ? 0.5 : 1,
            cursor: waiting ? 'default' : 'pointer',
          })}
        >
          <Camera size={16} />
          {countdown !== null ? 'Get ready...' : 'Take a selfie'}
        </button>
      ) : (
        <div style={{ display: 'flex', gap: 10, width: '100%', maxWidth: 400 }}>
          <button type="button" onClick={retake} className="fvd-hoverable" style={hubButtonStyle('secondary', isMobile, { flex: 'none', borderRadius: 'var(--fv-radius-button, 10px)' })}>
            <RotateCcw size={14} /> Retake
          </button>
          <button type="button" onClick={confirm} className="fvd-hoverable-solid" style={hubButtonStyle('primary', isMobile, { flex: 1, borderRadius: 'var(--fv-radius-button, 10px)' })}>
            {confirmLabel}
          </button>
        </div>
      )}

      <p style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--fvd-ink-muted)', margin: 0, maxWidth: 400 }}>
        Your selfie is used only to match your face with event photos, and is automatically deleted within 24 hours.
      </p>
    </div>
  );
}
